import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast as message } from 'sonner';
import { queryPolicies } from '../../../utils/queryPolicies';
import { useMyCapabilities } from '../../../utils/metadata/capabilities';
import DealPrepHistoryDialog from '../../DealPrep/DealPrepHistoryDialog';
import DealPrepChainActionDialog from '../../DealPrep/DealPrepChainActionDialog';
import {
  createMerchantDriveFolder,
  dealPrepKeys,
  fetchMerchantDealPrep,
  type DealPrepChainAction,
  type DealPrepChainView,
  type DealPrepIntakeView,
  type DealPrepMessageView,
} from '../../DealPrep/dealPrepApi';

const KIND_LABEL: Record<DealPrepMessageView['kind'], string> = {
  dealprep: 'DEAL PREP',
  append: 'Added to chain',
  alert: 'Alert',
  request: 'Statements request',
  reminder: 'Reminder',
  history: 'Submission history',
};

const VERDICT_LABEL: Record<DealPrepChainView['verdict'], string> = {
  new: 'New',
  shopping: 'Merchant is shopping',
  previous: 'Previous application',
  mismatch: 'Chain check: different merchant',
};

function fmt(at: string | null): string {
  if (!at) return '';
  return new Date(at).toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Deal Prep tab (REG-859): every DEAL PREP chain for this merchant, each
 * message with its envelope, flags and Gmail link, the applications behind it,
 * and (for deal editors) the intake jobs and merchant link checks. The RPC runs
 * as the user, so reps see only chains of merchants they can see and never the
 * intake or link-check details.
 */
function MerchantDealPrepTab({ merchantId }: { merchantId: number }) {
  const { hasCapability } = useMyCapabilities();
  const canSendHistory = hasCapability('send_deal_prep_history');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [chainAction, setChainAction] = useState<{ chain: DealPrepChainView; action: DealPrepChainAction } | null>(null);
  const queryClient = useQueryClient();
  // REG-734: find or create the merchant's Drive folder (same root and lookup as deal prep).
  const driveFolder = useMutation({
    mutationFn: () => createMerchantDriveFolder(merchantId),
    onSuccess: async (r) => {
      message.success(r.how === 'created' ? 'Drive folder created' : 'Drive folder found and linked');
      await queryClient.invalidateQueries({ queryKey: dealPrepKeys.merchant(merchantId) });
      await queryClient.invalidateQueries({ queryKey: ['merchant'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const q = useQuery({
    queryKey: dealPrepKeys.merchant(merchantId),
    queryFn: () => fetchMerchantDealPrep(merchantId),
    ...queryPolicies.recordDetail,
  });

  if (q.isLoading) return <div className="sky-mp-empty">Loading...</div>;
  if (q.error) return <div className="sky-mp-empty">{(q.error as Error).message}</div>;
  const view = q.data!;
  const unfinished = view.intake.filter((j) => j.status !== 'done');

  return (
    <div className="sky-mp-deal-prep-tab" data-testid="merchant-deal-prep-tab">
      <div className="sky-mp-dp-actions">
        {view.drive_folder_url && (
          <a className="sky-btn sky-btn-ghost" href={view.drive_folder_url} target="_blank" rel="noreferrer" data-testid="deal-prep-drive-link">
            Open Drive folder
          </a>
        )}
        {!view.drive_folder_url && canSendHistory && (
          <button className="sky-btn sky-btn-ghost" onClick={() => driveFolder.mutate()} disabled={driveFolder.isPending}
            data-testid="deal-prep-create-drive-folder">
            {driveFolder.isPending ? <span className="sky-spinner" /> : null}
            Create Drive folder
          </button>
        )}
        {canSendHistory && (
          <button className="sky-btn sky-btn-ghost" onClick={() => setHistoryOpen(true)} data-testid="deal-prep-send-history">
            Send submission history
          </button>
        )}
      </div>

      {unfinished.length > 0 && <IntakeIssues jobs={unfinished} />}

      {view.chains.length === 0 ? (
        <div className="sky-mp-summary">
          <div className="sky-mp-empty-inline">No DEAL PREP in the CRM for this merchant yet.</div>
        </div>
      ) : (
        view.chains.map((c) => (
          <ChainCard key={c.id} chain={c}
            onAction={canSendHistory ? (action) => setChainAction({ chain: c, action }) : undefined} />
        ))
      )}

      {view.link_checks.some((l) => !l.ok || l.verify) && (
        <div className="sky-mp-summary">
          <div className="sky-mp-summary-head"><span className="sky-mp-summary-label">MERCHANT LINK CHECKS</span></div>
          {view.link_checks.filter((l) => !l.ok || l.verify).map((l, i) => (
            <div key={i} className="sky-mp-dp-line">
              <span className={`sky-mp-dp-flag sky-mp-dp-flag--${l.ok ? 'amber' : 'red'}`}>
                {l.ok ? 'Linked, verify' : 'Not linked'}
              </span>{' '}
              {fmt(l.created_at)}: application &quot;{l.application_business ?? 'no name'}&quot; matched by {l.matched_by}
              {l.reason ? ` (${l.reason})` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Portaled: inside the side panel, the grid's transparent scroll layer sat on top of the
          dialog's left half and swallowed clicks (Cancel, the file list). */}
      {canSendHistory && createPortal(
        <DealPrepHistoryDialog merchantId={merchantId} isOpen={historyOpen} onClose={() => setHistoryOpen(false)} />,
        document.body,
      )}
      {canSendHistory && chainAction && createPortal(
        <DealPrepChainActionDialog merchantId={merchantId} chain={chainAction.chain} action={chainAction.action}
          isOpen onClose={() => setChainAction(null)} />,
        document.body,
      )}
    </div>
  );
}

function IntakeIssues({ jobs }: { jobs: DealPrepIntakeView[] }) {
  return (
    <div className="sky-mp-summary" data-testid="deal-prep-intake-issues">
      <div className="sky-mp-summary-head"><span className="sky-mp-summary-label">INTAKE</span></div>
      {jobs.map((j) => (
        <div key={j.job} className="sky-mp-dp-line">
          <span className={`sky-mp-dp-flag sky-mp-dp-flag--${j.status === 'failed' ? 'red' : 'amber'}`}>{j.status}</span>{' '}
          Application #{j.form}, {fmt(j.updated_at)}
          {j.outcome ? `: ${j.outcome}` : ''}
          {j.error ? ` (${j.error})` : ''}
          {j.status === 'queued' && j.attempts > 0 ? `, retry ${j.attempts}` : ''}
        </div>
      ))}
    </div>
  );
}

function ChainCard({ chain, onAction }: { chain: DealPrepChainView; onAction?: (action: DealPrepChainAction) => void }) {
  return (
    <div className="sky-mp-summary" data-testid="deal-prep-chain">
      <div className="sky-mp-summary-head">
        <span className="sky-mp-summary-label">{chain.subject}</span>
        <span className="sky-mp-sub-pills">
          <span className="sky-mp-lead-tag">{chain.brand}</span>
          {chain.verdict !== 'new' && <span className="sky-mp-lead-tag">{VERDICT_LABEL[chain.verdict]}</span>}
        </span>
      </div>
      <div className="sky-mp-dp-meta">
        Started {fmt(chain.created_at)}
        {chain.rep ? ` · ${chain.rep}` : ''}
        {' · from '}{chain.sender_email}
        {chain.drive_folder_url && (
          <>
            {' · '}
            <a href={chain.drive_folder_url} target="_blank" rel="noreferrer">Drive</a>
          </>
        )}
      </div>
      {chain.applications.length > 0 && (
        <div className="sky-mp-dp-meta">
          {chain.applications.map((a) => `${a.provider ?? 'e-sign'} application #${a.form}, signed ${fmt(a.signed_at)}`).join(' · ')}
        </div>
      )}
      {onAction && (
        <div className="sky-mp-dp-actions" style={{ marginTop: 6 }}>
          <button className="sky-btn sky-btn-ghost" onClick={() => onAction('resend')} data-testid="deal-prep-resend">Resend deal prep</button>
          <button className="sky-btn sky-btn-ghost" onClick={() => onAction('append_latest')} data-testid="deal-prep-append-latest">Append latest documents</button>
        </div>
      )}
      <div className="sky-mp-sub-list" style={{ marginTop: 8 }}>
        {chain.messages.map((m) => <MessageRow key={m.id} m={m} />)}
      </div>
    </div>
  );
}

function MessageRow({ m }: { m: DealPrepMessageView }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="sky-mp-sub-card" data-testid="deal-prep-message">
      <div className="sky-mp-sub-card-top">
        <div className="sky-mp-sub-card-info">
          <span className="sky-mp-sub-date">{fmt(m.sent_at ?? m.created_at)}</span>
          <span className="sky-mp-sub-lender">{KIND_LABEL[m.kind]}</span>
        </div>
        <span className="sky-mp-sub-pills">
          {m.sandbox && <span className="sky-mp-lead-tag" title="Sandbox: sent only to the sandbox recipient">Sandbox</span>}
          <span className={`sky-mp-lead-tag ${m.status === 'failed' ? 'sky-mp-dp-tag--failed' : ''}`}>{m.status}</span>
          {m.gmail_url && <a href={m.gmail_url} target="_blank" rel="noreferrer" className="sky-mp-lead-tag">Gmail</a>}
        </span>
      </div>
      {m.flags.length > 0 && (
        <div className="sky-mp-dp-flags">
          {m.flags.map((f, i) => <div key={i} className={`sky-mp-dp-flag sky-mp-dp-flag--${f.level}`}>{f.text}</div>)}
        </div>
      )}
      {m.attachments.length > 0 && (
        <div className="sky-mp-dp-meta">{m.attachments.map((a) => a.filename).join(', ')}</div>
      )}
      {m.error && <div className="sky-mp-dp-flag sky-mp-dp-flag--red">{m.error}</div>}
      <button className="sky-mp-dp-toggle" onClick={() => setOpen(!open)}>{open ? 'Hide envelope' : 'Envelope'}</button>
      {open && (
        <div className="sky-mp-dp-meta">
          <div>From {m.from_email}</div>
          <div>To {m.to.join(', ')}{m.cc.length ? `; Cc ${m.cc.join(', ')}` : ''}</div>
          {m.sandbox && (
            <div>Without sandbox: To {m.intended_to.join(', ')}{m.intended_cc.length ? `; Cc ${m.intended_cc.join(', ')}` : ''}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default MerchantDealPrepTab;
